import { usePostHog } from '@posthog/react';
import SearchBar from '@sharedComponents/SearchBar';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';

/**
 * Search input driving an InstantSearch context. Must be rendered inside
 * `<InstantSearch>`.
 *
 * `agencyId` is only used to qualify the PostHog event; the event
 * name is kept as the booking catalogue emitted it historically so existing
 * dashboards keep working.
 *
 * `indexes` gives the relevance / alphabetical index names; it must match the one given
 * to `AlgoliaIndexSwitch`.
 *
 * Requires `AlgoliaIndexSwitch` alongside it: the index this component selects is
 * carried by the sort-by state, which only exists while that widget is mounted.
 */
const AlgoliaSearchBar = ({ agencyId, indexes }) => {
    const { query } = useSearchBox();
    const { setIndexUiState } = useInstantSearch();
    const { t } = useTranslation();
    const posthog = usePostHog();
    const debounceRef = useRef(null);
    const searchBarRef = useRef(null);
    const lastRefinedRef = useRef('');

    // The SearchBar owns its text state: when the query changes from outside the
    // input (e.g. "Reset filters"), the new value must be pushed into it. Values we
    // refined ourselves are skipped so typing is never overwritten.
    useEffect(() => {
        if (query !== lastRefinedRef.current) {
            // A pending debounced refine would re-apply the text the reset just cleared.
            if (debounceRef.current) clearTimeout(debounceRef.current);
            lastRefinedRef.current = query;
            searchBarRef.current?.setInputValue(query);
        }
    }, [query]);

    // Query and index have to move in the same state update. Refining the query alone
    // would first search whichever index is in place, rendering a frame of results in the
    // wrong order before the switch lands.
    const applyQuery = (value) => {
        lastRefinedRef.current = value;
        setIndexUiState((previous) => ({
            ...previous,
            query: value || undefined,
            sortBy: value.trim() ? indexes.relevance : indexes.alphabetical,
            page: undefined,
        }));
    };

    const handleTextChange = (value) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            applyQuery(value);
            posthog?.capture('booking_catalog:search', { agencyId, searchText: value });
        }, 300);
    };

    return (
        <div style={{ marginBottom: 'var(--space-xs)' }}>
            <SearchBar
                ref={searchBarRef}
                placeholder={t('Search for a test, a profession, a skill...')}
                minChars={0}
                hideMenu
                controlShouldRenderValue={false}
                search={async () => []}
                onTextChange={handleTextChange}
                onClear={() => {
                    if (debounceRef.current) clearTimeout(debounceRef.current);
                    applyQuery('');
                }}
            />
        </div>
    );
};

AlgoliaSearchBar.propTypes = {
    agencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    indexes: PropTypes.shape({ relevance: PropTypes.string.isRequired, alphabetical: PropTypes.string.isRequired }).isRequired,
};

export default AlgoliaSearchBar;
